import React, { useContext } from 'react'

import { Box, DisplayPosition } from '@gousto-internal/citrus-react'

import type { RecipeGridContentBlock } from '@library/api-menu-experience'
import { RecipeGrid } from '@library/menu-page'
import type { RecipeUUID } from '@library/menu-service'

import {
  CaloriesTutorial,
  useShouldShowCaloriesTutorial,
} from 'routes/Menu/components/MenuPreferences'
import { useSetBrowserCTAVisibility } from 'routes/Menu/domains/menu'

import { MenuBrandingContext } from '../menuBrandingContext'
import { useMenuExperienceRecipeDetails } from '../menuExperienceRecipeDetailContext'
import { useRecipeGridBasket } from './useRecipeGridBasket'
import { useRecipeGridCustomisationData } from './useRecipeGridCustomisationData'

type RecipeGridContentBlockWrapperProps = {
  contentBlock: RecipeGridContentBlock
  collectionId?: string
}

export const RecipeGridContentBlockWrapper = ({
  contentBlock,
  collectionId,
}: RecipeGridContentBlockWrapperProps) => {
  const { textColor } = useContext(MenuBrandingContext)
  const { openRecipeDetail } = useMenuExperienceRecipeDetails()
  const shouldShowCaloriesTutorial = useShouldShowCaloriesTutorial(collectionId)
  const { setBrowserCTAVisible } = useSetBrowserCTAVisibility()
  const { getRecipeTileState, onAddClick, onRemoveClick } = useRecipeGridBasket(contentBlock.items)
  const { getRecipeCustomisationData, openRecipeCustomisations, closeRecipeCustomisations } =
    useRecipeGridCustomisationData({ collectionId })

  const onRecipeClick = (recipeId: RecipeUUID, recipePosition: number) => {
    const recipe = contentBlock.items.find((item) => item.id === recipeId)

    if (!recipe) {
      return
    }

    openRecipeDetail({
      recipeId,
      title: recipe.title,
      position: recipePosition.toString(),
      source: 'recipe_grid',
    })
  }

  const supplementaryItems = shouldShowCaloriesTutorial
    ? [
        {
          id: 'calories-tutorial',
          position: 5 as const,
          content: (
            <Box position={DisplayPosition.Relative} width="100%">
              <CaloriesTutorial />
            </Box>
          ),
        },
      ]
    : []

  return (
    <RecipeGrid
      contentBlock={contentBlock}
      recipeCountTextColor={textColor}
      supplementaryItems={supplementaryItems}
      getRecipeTileState={getRecipeTileState}
      getRecipeCustomisationData={getRecipeCustomisationData}
      onRecipeClick={onRecipeClick}
      onAddClick={onAddClick}
      onRemoveClick={onRemoveClick}
      onBlockedAddAttempt={setBrowserCTAVisible}
      onCustomiseOpen={openRecipeCustomisations}
      onCustomiseClose={closeRecipeCustomisations}
    />
  )
}
